import { track } from '@amplitude/analytics-browser'
import { yupResolver } from '@hookform/resolvers/yup'
import { useRequest } from 'ahooks'
import axios, { AxiosError } from 'axios'
import useNavigate, { useLocation } from 'hooks/useNavigate'
import { ReactElement, useEffect, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useForm } from 'react-hook-form'
import { CodeField, SubmitButton, Subtitle, Title, Typography } from '~components'
import TimerButton from '~components/smarts/TimerButton'
import { usePathValidation, useStore } from '~hooks'
import { Auth } from '~services'
import { array, object, string } from '~utils/validation'
import * as Interfaces from './interfaces'
import * as Styled from './styled'

const OtpCode = (): ReactElement => {
  const location = useLocation()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const option = location?.state?.['option']
  const currentMethod =
    option === 'sms' ? Interfaces.MethodEnum.SMS : Interfaces.MethodEnum.WHATSAPP

  const defaultValues = {
    token: [],
  }
  const navigate = useNavigate()
  const store = useStore()
  const [error, setError] = useState('')
  const schema = object({
    token: array().min(4).of(string().required()),
  })
  const {
    handleSubmit,
    setFocus,
    register,
    getValues,
    setValue,
    formState: { isDirty, isValid },
  } = useForm<Interfaces.Values>({
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onChange',
  })
  const submitButtonDisabled = !isValid || !isDirty
  const hasSomethingTyped = isDirty
  const { loading: verifyingPasswordLoading, runAsync: verifyResetPasswordCode } =
    useRequest<Interfaces.DataSMSCode, Interfaces.Params>(Auth.verifyResetPasswordCode, {
      manual: true,
    })
  const { executeRecaptcha } = useGoogleReCaptcha()

  const { loading: sendingPasswordLoading, runAsync: sendResetPasswordCode } = useRequest(
    Auth.sendResetPasswordCode,
    {
      manual: true,
    },
  )

  usePathValidation(['cpf', 'passwordRecovery.cellphone'])

  async function resendPasswordCode() {
    const recaptchaToken = await executeRecaptcha()
    await sendResetPasswordCode(
      store.cpf,
      store.passwordRecovery.cellphone?.id,
      currentMethod,
      recaptchaToken,
    )
  }

  function handleChange() {
    if (error) {
      setError('')
    }
  }

  useEffect(() => {
    resendPasswordCode()
  }, [option])

  async function onSubmit(values: Interfaces.Values) {
    try {
      const recaptchaToken = await executeRecaptcha('action')
      const data = await verifyResetPasswordCode(
        store.cpf,
        store.passwordRecovery.cellphone?.id,
        values.token.join(''),
        currentMethod,
        recaptchaToken,
      )

      localStorage.setItem('confirmation_token', data.confirmation_token)
      track('ignito__recuperacao_de_senha__seleciona_telefone__validacao_otp_valida')
      navigate('/recuperar-senha-motorista/redefinir', {
        state: { previousPath: '/recuperar-senha-motorista/validar' },
      })
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<{ blocked_until: string }>
        if (axiosError.response.data.blocked_until) {
          store.update({
            passwordRecovery: { blockedTime: axiosError.response.data.blocked_until },
          })
          navigate('/recuperar-senha-motorista/dificuldade', {
            state: { previousPath: '/recuperar-senha-motorista/validar' },
          })
        }
      }
      track('ignito__recuperacao_de_senha__seleciona_telefone__validacao_otp_invalida')
      setError('Código inválido.')
    }
  }

  const subtitle = `Digite o código de 4 dígitos que enviamos por ${
    currentMethod === Interfaces.MethodEnum.SMS ? 'SMS' : 'WhatsApp'
  } para o telefone`

  return (
    <form onSubmit={handleSubmit(onSubmit)} aria-labelledby="form-cellphone-code-title">
      <Title id="form-cellphone-code-title">Verifique o seu celular</Title>
      <Subtitle>
        {subtitle}{' '}
        <Typography as="b">{store.passwordRecovery.cellphone?.number}.</Typography>
      </Subtitle>
      <CodeField
        name="token"
        register={register}
        setFocus={setFocus}
        error={error}
        getValues={getValues}
        onChange={handleChange}
        hasSomethingTyped={hasSomethingTyped}
        setValue={setValue}
      />
      <SubmitButton
        disabled={submitButtonDisabled}
        width="100%"
        loading={verifyingPasswordLoading}
      >
        Continuar
      </SubmitButton>
      <Styled.Footer>
        <Typography as="p">Não recebeu o código?</Typography>

        <TimerButton
          onClick={resendPasswordCode}
          loading={sendingPasswordLoading}
          currentMethod={currentMethod}
        />
      </Styled.Footer>
    </form>
  )
}

export default OtpCode
